import { Button } from '@components/Elements/Button'
import { Dialog } from '@components/Elements/Dialog'
import { CodeInput, DateInputSimple } from '@components/Elements/Input'
import { InlineLink } from '@components/Elements/Text'
import { Markdown } from '@components/Markdown'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDebounce } from '@lib/hooks'
import { useI18n } from '@lib/i18n'
import { TrackingEvent, trackEvent } from '@lib/tracking'
import { getInputSchema, Input } from '@lib/validation'
import { DeepLinkParams, isDeepLinkValid, OnboardingStep, useOnboardingStore } from '@stores/onboarding'
import { useToastsStore } from '@stores/toasts'
import dayjs from 'dayjs'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Platform, TextInput } from 'react-native'
import styled from 'styled-components/native'
import {
  ButtonWrapper,
  ContentWrapper,
  ErrorMessage,
  Label,
  OnboardingAnimationWrapper,
  Title,
  useOnboardingAnimation,
} from './styles'

const DialogWrapper = styled.View`
  margin-top: 10px;
`
const MarkdownWrapper = styled.View`
  margin-top: 26px;
  margin-bottom: 15px;
`

export function Otp({ params }: { params?: DeepLinkParams }) {
  const t = useI18n()
  const otpInputRef = useRef<TextInput>(null)
  const dateInputRef = useRef<TextInput>(null)
  const [error, setError] = useState('')
  const [isDialogVisible, setDialogVisibility] = useState(false)
  const style = useOnboardingAnimation()
  const [addToast] = useToastsStore((state) => [state.addToast])
  const [input, requestOtp, verifyOtp, updateInput, hasDobVerification, replace] = useOnboardingStore((state) => [
    state.input,
    state.requestOtp,
    state.verifyOtp,
    state.updateInput,
    state.hasDobVerification,
    state.replace,
  ])

  const inputFields: Array<keyof Input> = ['otp']

  if (hasDobVerification) inputFields.push('dateOfBirth')

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    getValues,
    reset,
    setValue,
  } = useForm<{ otp?: string; dateOfBirth?: Date }>({
    resolver: yupResolver(getInputSchema().pick(hasDobVerification ? ['otp', 'dateOfBirth'] : ['otp'])),
    defaultValues: {
      otp: params?.otp || input.otp,
      ...(hasDobVerification && {
        dateOfBirth: input.dateOfBirth ? dayjs(input.dateOfBirth).toDate() : undefined,
      }),
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const handleFormSubmit = useDebounce(() => {
    if (params?.otp && !isDeepLinkValid(params)) {
      replace([OnboardingStep.INTRO, OnboardingStep.EXPIRED])
      return
    }

    handleSubmit(
      ({ otp, dateOfBirth }) => {
        updateInput({
          otp,
          ...(hasDobVerification && { dateOfBirth }),
        })
        verifyOtp()
          .then(() => trackEvent(TrackingEvent.onboardingSignedIn))
          .catch((error: Error) => {
            if (error?.cause === 'InvalidOtpError') {
              if (params?.otp) {
                // otp not expired but already used
                replace([OnboardingStep.INTRO, OnboardingStep.EXPIRED])
              } else {
                setError(t('error.invalidOtp', { otp }))
                reset({ otp: '', dateOfBirth: getValues()?.dateOfBirth })
              }
            } else if (error?.cause === 'IncorrectDobError') {
              setError(t('error.invalidDob'))
            } else if (error?.cause === 'AccountIsLockedError') {
              setError(t('error.accountLocked'))
            } else if (error?.cause === 'TooManyRequestsError') {
              setError(t('error.tooManyRequests'))
            }
          })
      },
      () => {
        setError('')
      },
    )()
  }, [params])

  const handleDialog = useDebounce(() => {
    otpInputRef.current?.blur()
    setDialogVisibility(true)
  })

  const handleResendCode = useDebounce(async () => {
    trackEvent(TrackingEvent.onboardingResendCodeConfirmPressed)
    await requestOtp().then(() => {
      setDialogVisibility(false)
      addToast({
        message: t('onboarding.newCodeSent'),
      })
    })
  })

  function handleOtpSubmit(): void {
    if (!hasDobVerification) {
      handleFormSubmit()
    } else if (!getValues().dateOfBirth) {
      dateInputRef.current?.focus()
    }
  }

  useEffect(() => {
    if (params?.userId) dateInputRef.current?.focus()
  }, [params?.userId])

  useEffect(() => {
    if (params?.otp) setValue('otp', params.otp)
  }, [params?.otp])

  return (
    <OnboardingAnimationWrapper style={style}>
      <ContentWrapper>
        {!!error && <ErrorMessage message={error} />}
        <Title>{t('onboarding.verify')}</Title>
        {!params?.otp && (
          <>
            <MarkdownWrapper>
              <Markdown
                text={
                  input.phone
                    ? t('onboarding.sentCodeToNumber', {
                        phone: parsePhoneNumberWithError(input.phone).format('INTERNATIONAL'),
                      })
                    : t('onboarding.sentCodeToYou')
                }
                textStyle={{ weight: 'medium' }}
                withoutParagraphSpacing
              />
            </MarkdownWrapper>
            <Controller
              control={control}
              name="otp"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CodeInput
                  ref={otpInputRef}
                  length={6}
                  value={value}
                  onChange={onChange}
                  onSubmitEditing={handleOtpSubmit}
                  error={error?.message}
                  enterKeyHint={Platform.select({ ios: 'next', android: 'next' })}
                  inputMode="numeric"
                  placeholder={t('onboarding.placeholder.code')}
                  autoFocus
                />
              )}
            />
          </>
        )}
        {hasDobVerification && (
          <>
            <Label>{params?.otp ? t('onboarding.short.enterDateOfBirth') : t('onboarding.enterDateOfBirth')}</Label>
            <Controller
              control={control}
              name="dateOfBirth"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <DateInputSimple
                  ref={dateInputRef}
                  date={value && dayjs(value)}
                  onChange={(date) => onChange(date?.toDate())}
                  onSubmitEditing={handleFormSubmit}
                  error={error?.message}
                />
              )}
            />
          </>
        )}
        <ButtonWrapper>
          <Button fullWidth isLoading={isSubmitting} onPress={handleFormSubmit} label={t('actions.done')} />
        </ButtonWrapper>
        {input.phone ? (
          <InlineLink
            onPress={handleDialog}
            weight="semiBold"
            hasUnderline={false}
            margins={{ top: 10 }}
            isCentered
            size="button"
          >
            {t('onboarding.dialog.otp.message')}
          </InlineLink>
        ) : null}
      </ContentWrapper>
      <Dialog
        isVisible={isDialogVisible}
        title={t('onboarding.dialog.otp.title')}
        primaryAction={{
          label: t('actions.ok'),
          onPress: () => setDialogVisibility(false),
        }}
      >
        <DialogWrapper>
          <Markdown text={t('onboarding.dialog.account.otp')} textStyle={{ weight: 'semiBold', fontSize: 'medium' }} />
          <Markdown
            text={t('onboarding.dialog.otp.troubleshooting')}
            listStyle={{ fontSize: 'medium' }}
            customActions={{ fn: handleResendCode }}
          />
        </DialogWrapper>
      </Dialog>
    </OnboardingAnimationWrapper>
  )
}
